import { HARBOR_COMPARE_SLUGS, HARBOR_FACTS } from '@/lib/harbor-facts';

export const COMPETITOR_SLUGS = HARBOR_COMPARE_SLUGS;

export type CompetitorSlug = (typeof COMPETITOR_SLUGS)[number];

export type CompetitorFeatureKey =
  | 'householdPrice'
  | 'billingUnit'
  | 'messaging'
  | 'messageSearchBookmarks'
  | 'toneCheck'
  | 'expenseTracking'
  | 'custodyCalendar'
  | 'soloUse'
  | 'gpsCheckIns'
  | 'professionalAccess'
  | 'courtReadyRecords'
  | 'platforms';

export type ComparisonRating = 'good' | 'mixed' | 'bad';

export type ComparisonCell = {
  text: string;
  /** Leave unset on Price cells — those stay plain text. */
  rating?: ComparisonRating;
};

export type CompetitorFeatureMatrix = Record<CompetitorFeatureKey, ComparisonCell>;

export const COMPARISON_RATING_EMOJI: Record<ComparisonRating, string> = {
  good: '✅',
  mixed: '⚠️',
  bad: '❌',
};

export function formatComparisonCell({ text, rating }: ComparisonCell): string {
  if (!rating) return text;
  return `${COMPARISON_RATING_EMOJI[rating]} ${text}`;
}

export type CompetitorFaq = {
  question: string;
  answer: string;
};

export type CompetitorProfile = {
  slug: CompetitorSlug;
  name: string;
  tagline: string;
  seoTitle?: string;
  seoDescription?: string;
  pricingMonthly: string;
  pricingAnnual: string;
  pricingSourceUrl: string;
  pricingVerifiedDate: string;
  bestFor: string;
  whenToPickThem: string;
  verdict: string;
  vsHarbor: string;
  pros: string[];
  cons: string[];
  featureMatrix: CompetitorFeatureMatrix;
  faqs: CompetitorFaq[];
  highlightPrice?: boolean;
};

export const COMPARISON_FEATURES: Array<{
  key: CompetitorFeatureKey;
  label: string;
  harborValue: ComparisonCell;
}> = [
  {
    key: 'householdPrice',
    label: 'Price',
    harborValue: { text: `${HARBOR_FACTS.monthlyPriceLabel} or ${HARBOR_FACTS.annualPriceLabel}` },
  },
  {
    key: 'billingUnit',
    label: 'Who pays',
    harborValue: { text: 'Per parent', rating: 'good' },
  },
  {
    key: 'messaging',
    label: 'Documented messaging',
    harborValue: {
      text: 'Timestamped, non-editable chat with topics and read receipts',
      rating: 'good',
    },
  },
  {
    key: 'messageSearchBookmarks',
    label: 'Search and bookmark messages',
    harborValue: {
      text: 'Full-text search plus private bookmark folders',
      rating: 'good',
    },
  },
  {
    key: 'toneCheck',
    label: 'Tone check before send',
    harborValue: {
      text: 'Temperature Check scores tone and offers a calmer rewrite',
      rating: 'good',
    },
  },
  {
    key: 'expenseTracking',
    label: 'Expenses',
    harborValue: {
      text: 'Splits, payments, receipt OCR, and exports in one ledger',
      rating: 'good',
    },
  },
  {
    key: 'custodyCalendar',
    label: 'Custody calendar',
    harborValue: {
      text: 'Schedules, exceptions, and Google / Apple / Outlook sync',
      rating: 'good',
    },
  },
  {
    key: 'soloUse',
    label: 'Works with one parent',
    harborValue: { text: 'Yes: start solo and keep a record', rating: 'good' },
  },
  {
    key: 'gpsCheckIns',
    label: 'GPS exchange check-ins',
    harborValue: {
      text: 'Location-verified handoffs with on-time / late tracking',
      rating: 'good',
    },
  },
  {
    key: 'professionalAccess',
    label: 'Professional access',
    harborValue: {
      text: 'Read-only invites for lawyers, mediators, and coaches',
      rating: 'good',
    },
  },
  {
    key: 'courtReadyRecords',
    label: 'Court-ready exports',
    harborValue: { text: 'One-tap message, expense, and calendar reports', rating: 'good' },
  },
  {
    key: 'platforms',
    label: 'Platforms',
    harborValue: { text: HARBOR_FACTS.platforms, rating: 'good' },
  },
];

export const COMPETITORS: CompetitorProfile[] = [
  {
    slug: 'ourfamilywizard',
    name: 'OurFamilyWizard',
    tagline: 'The court-familiar incumbent with per-parent pricing and ToneMeter.',
    seoTitle: 'OurFamilyWizard pricing 2026: Harbor vs OFW ($50/year)',
    seoDescription:
      'OurFamilyWizard pricing 2026 versus Harbor. Temperature Check vs ToneMeter, expenses, and court-ready records. Harbor is $49.99/year per person.',
    pricingMonthly: 'From about $12.50 per parent/month on annual Essentials billing',
    pricingAnnual: 'Typically $220–$600/year for two parents before add-ons',
    pricingSourceUrl: 'https://www.ourfamilywizard.com/pricing',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families whose court order names OurFamilyWizard, or attorneys who already work in it.',
    whenToPickThem:
      'Pick OurFamilyWizard when a judge, GAL, or parenting coordinator has already named it, or when the other parent will only join a platform they already know. Harbor is the better first choice when you can choose the app: $49.99/year per person, Temperature Check, and daily expenses plus calendar in one place.',
    verdict:
      'OurFamilyWizard is the most court-familiar co-parenting app. Harbor is usually the better value for families who are not bound to a named platform: $49.99/year per person versus OFW Essentials around $12.50 per parent per month.',
    vsHarbor:
      'Harbor and OurFamilyWizard both cover documented messaging, expenses, calendars, and exports. The practical split is price and daily friction: Harbor is $9.99/month or $49.99/year per person, while OurFamilyWizard bills each parent at a higher typical rate and is the name most often written into court orders. If an order names OurFamilyWizard, use OurFamilyWizard.',
    pros: [
      'Widely recognized by family-law professionals and often named in orders',
      'ToneMeter plus writing assistance on higher tiers',
      'Deep add-on ecosystem and professional access',
    ],
    cons: [
      'Per-parent pricing makes a two-parent household much more expensive',
      'Some features sit behind higher tiers or add-ons',
      'Heavier interface for quick daily use',
    ],
    highlightPrice: true,
    featureMatrix: {
      householdPrice: { text: '$24.99/month or $299.88/year' },
      billingUnit: { text: 'Per parent', rating: 'good' },
      messaging: { text: 'Documented messaging with professional access', rating: 'good' },
      messageSearchBookmarks: {
        text: 'Keyword search and private message folders',
        rating: 'good',
      },
      toneCheck: {
        text: 'ToneMeter and optional writing assistant on higher tiers',
        rating: 'mixed',
      },
      expenseTracking: {
        text: 'Expense log with reimbursements; some tools are add-ons',
        rating: 'mixed',
      },
      custodyCalendar: { text: 'Shared calendar with requests and journal', rating: 'good' },
      soloUse: {
        text: 'Usable by one parent; the other parent is expected to join',
        rating: 'mixed',
      },
      gpsCheckIns: { text: 'Available on higher tiers', rating: 'mixed' },
      professionalAccess: {
        text: 'Free professional accounts for attorneys and coordinators',
        rating: 'good',
      },
      courtReadyRecords: {
        text: 'Court-ready exports and a long legal track record',
        rating: 'good',
      },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is Harbor cheaper than OurFamilyWizard for two parents?',
        answer:
          'Yes in typical two-parent setups. Harbor is $9.99/month or $49.99/year per person. OurFamilyWizard lists Essentials around $12.50 per parent per month on annual billing, so two parents often pay $220–$600/year before add-ons. Confirm current OFW pricing on their site.',
      },
      {
        question: 'Does Harbor have a tone checker like OurFamilyWizard ToneMeter?',
        answer:
          'Yes. Harbor Temperature Check scores a draft in conversation context and offers a calmer, BIFF-style rewrite before you send. OurFamilyWizard ToneMeter is the closest incumbent feature and usually sits on paid tiers.',
      },
      {
        question: 'Can I search and bookmark messages in Harbor and OurFamilyWizard?',
        answer:
          'Yes on both. Harbor lets you search past messages and save them to private bookmark folders. OurFamilyWizard has keyword search plus private message folders for organizing threads.',
      },
      {
        question: 'Should I use Harbor if my court order names OurFamilyWizard?',
        answer:
          'No. If a court order names OurFamilyWizard, use OurFamilyWizard. Harbor is an alternative when you can choose the app, not a substitute for a named platform.',
      },
      {
        question: 'Do both apps support court-ready records?',
        answer:
          'Both export timestamped communication and expense records for attorneys. Harbor calls these court-ready exports; it does not claim guaranteed admissibility. OurFamilyWizard has a longer history of being named in orders.',
      },
      {
        question: 'Can I start Harbor if my co-parent will not join yet?',
        answer:
          'Yes. Harbor works solo: you can log expenses, keep a calendar, and build a record before the other parent accepts an invite.',
      },
    ],
  },
  {
    slug: 'talkingparents',
    name: 'TalkingParents',
    tagline: 'Communication-first records app with per-parent plans and recorded calls on higher tiers.',
    seoTitle: 'Harbor vs TalkingParents: Co-Parenting App Comparison (2026)',
    seoDescription:
      'Harbor vs TalkingParents on pricing, documented messaging, expenses, and tone check. Harbor is $49.99/year per person.',
    pricingMonthly: 'From about $7 per parent/month on Essentials; higher tiers $16–$32',
    pricingAnnual: 'Two-parent annual cost often $168–$768 depending on tier',
    pricingSourceUrl: 'https://talkingparents.com/pricing',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families whose main need is unalterable communication records or recorded calls.',
    whenToPickThem:
      'Pick TalkingParents when documented calls or a communication-only record is the requirement, or when an order names it. Pick Harbor when you also need expenses, a custody calendar, and tone checking in one app.',
    verdict:
      'TalkingParents is strong for communication monitoring. Harbor is the broader daily stack at $49.99/year per person, with Temperature Check and integrated expenses.',
    vsHarbor:
      'Both keep documented messages. TalkingParents is built around an unalterable communication record and, on higher tiers, recorded calls. Harbor adds a full expense ledger, custody calendar, Temperature Check, and a $49.99/year per-person plan. If an order names TalkingParents, use TalkingParents.',
    pros: [
      'Unalterable message record with verification codes on exports',
      'Recorded calls and transcription on higher tiers',
      'Familiar to many family-law professionals',
    ],
    cons: [
      'Per-parent pricing; two parents pay twice',
      'Expense and calendar tools are thinner than an all-in-one app',
      'Free mobile tier was removed in 2026',
    ],
    featureMatrix: {
      householdPrice: { text: 'From ~$7/parent/month; two parents often $168–$768/year' },
      billingUnit: { text: 'Per parent', rating: 'good' },
      messaging: {
        text: 'Unalterable documented messaging; recorded calls on higher tiers',
        rating: 'good',
      },
      messageSearchBookmarks: {
        text: 'Keyword search and topic threads; no bookmark folders',
        rating: 'mixed',
      },
      toneCheck: { text: 'No tone checker', rating: 'bad' },
      expenseTracking: {
        text: 'Basic expense tools; some families add a second app',
        rating: 'mixed',
      },
      custodyCalendar: {
        text: 'Calendar available; communication is the core product',
        rating: 'mixed',
      },
      soloUse: {
        text: 'Usable by one parent; the record is strongest when both join',
        rating: 'mixed',
      },
      gpsCheckIns: { text: 'Not a core GPS exchange product', rating: 'bad' },
      professionalAccess: {
        text: 'Exports and verification codes; no Harbor-style read-only portal',
        rating: 'mixed',
      },
      courtReadyRecords: {
        text: 'Unalterable communication exports with authentication codes',
        rating: 'good',
      },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is Harbor cheaper than TalkingParents for two parents?',
        answer:
          'Usually yes. Harbor is $49.99/year per person. TalkingParents bills each parent; Essentials is often listed near $7/month per parent and higher tiers cost more. Two parents on paid plans commonly spend $168–$768/year.',
      },
      {
        question: 'Does TalkingParents have a tone checker like Harbor?',
        answer:
          'No. Harbor Temperature Check scores draft tone and offers a rewrite. TalkingParents focuses on keeping the sent record unalterable rather than coaching the draft.',
      },
      {
        question: 'Can I search and bookmark messages in TalkingParents?',
        answer:
          'TalkingParents lets you search past conversations and organize threads by topic. Harbor adds bookmark folders so you can save specific messages to revisit later.',
      },
      {
        question: 'Which app is better if I only need documented messages?',
        answer:
          'TalkingParents is the communication-record specialist, including recorded calls on higher tiers. Harbor is better if you also want expenses, a custody calendar, and Temperature Check.',
      },
      {
        question: 'Should I use Harbor if my court order names TalkingParents?',
        answer:
          'No. Use the named app. Harbor is an alternative when you can choose the platform.',
      },
      {
        question: 'Can Harbor replace TalkingParents for expenses?',
        answer:
          'Yes for most families. Harbor keeps splits, payments, receipt scans, and exports in the same app as messaging and the calendar.',
      },
    ],
  },
  {
    slug: '2houses',
    name: '2houses',
    tagline: 'Family-priced household organizer with messaging, calendar, and expenses.',
    seoTitle: '2houses pricing 2026: Harbor vs 2houses',
    seoDescription:
      '2houses pricing 2026 versus Harbor on messaging, expenses, Temperature Check, and court-ready exports for US and Canadian co-parents.',
    pricingMonthly: 'Commonly listed near $9.99–$12.99/month per family',
    pricingAnnual: 'Often about $99/year for the household',
    pricingSourceUrl: 'https://www.2houses.com/en/pricing',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families who want one shared account and everyday household coordination.',
    whenToPickThem:
      'Pick 2houses if you already use it and mainly need a shared family organizer. Pick Harbor for Temperature Check, GPS exchange check-ins, and court-ready exports at $49.99/year per person.',
    verdict:
      '2houses is a capable family-priced organizer. Harbor is usually the better fit for US and Canadian co-parents who want tone checking, exchange check-ins, and exportable records at a lower annual price.',
    vsHarbor:
      '2houses bills a family plan, often near $99/year. Harbor is $9.99/month or $49.99/year per person, with Temperature Check, GPS check-ins, and one-tap exports. 2houses is a broader household organizer, with less emphasis on US-style court-ready workflows.',
    pros: [
      'Family-level pricing instead of per-parent seats',
      'Messaging, calendar, and expenses in one product',
      'Web and mobile access',
    ],
    cons: [
      'Usually costs more per year than Harbor',
      'No Temperature Check equivalent',
      'Lighter court-ready export positioning for US practice',
    ],
    featureMatrix: {
      householdPrice: { text: 'Often ~$99/year or $9.99–$12.99/month per family' },
      billingUnit: { text: 'Household / family', rating: 'good' },
      messaging: { text: 'In-app messaging and info sharing', rating: 'mixed' },
      messageSearchBookmarks: {
        text: 'Archive and PDF export; no published search or bookmarks',
        rating: 'mixed',
      },
      toneCheck: { text: 'No tone checker', rating: 'bad' },
      expenseTracking: { text: 'Shared expenses and reimbursements', rating: 'good' },
      custodyCalendar: { text: 'Shared parenting calendar', rating: 'good' },
      soloUse: { text: 'Works with one parent; designed for both households', rating: 'good' },
      gpsCheckIns: { text: 'Not a GPS exchange-check-in product', rating: 'bad' },
      professionalAccess: {
        text: 'Limited compared with Harbor professional invites',
        rating: 'mixed',
      },
      courtReadyRecords: {
        text: 'Exports vary; not Harbor’s one-tap court-ready workflow',
        rating: 'mixed',
      },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is Harbor cheaper than 2houses?',
        answer:
          'Harbor is $49.99/year per person. 2houses is often listed near $99/year or about $10–$13/month. Confirm current 2houses pricing on their site.',
      },
      {
        question: 'Does 2houses have a tone checker?',
        answer:
          'No. Harbor Temperature Check scores a draft and offers a calmer rewrite before send.',
      },
      {
        question: 'Can I search and bookmark messages in 2houses?',
        answer:
          '2houses can archive conversations and export them as PDFs. Harbor includes full-text message search and bookmark folders for saving specific messages.',
      },
      {
        question: 'Which app is better for court-ready records?',
        answer:
          'Harbor is built around one-tap message, expense, and calendar exports. 2houses is stronger as a household organizer than as a records-export workflow.',
      },
      {
        question: 'Do both apps work if only one parent joins?',
        answer:
          'Yes. Harbor is designed so one parent can start and keep a record until the other accepts.',
      },
    ],
  },
  {
    slug: 'appclose',
    name: 'AppClose',
    tagline: 'Former free default that now charges per parent for core co-parenting tools.',
    seoTitle: 'Harbor vs AppClose: Pricing After AppClose Went Paid (2026)',
    seoDescription:
      'AppClose is no longer the free co-parenting app. Compare Harbor’s $49.99/year per-person plan with AppClose’s per-parent subscription, messaging, expenses, and records.',
    pricingMonthly: 'About $7.99/month on web or $8.99/month in-app, per parent',
    pricingAnnual: 'Two parents often about $192–$216/year; hardship waivers exist',
    pricingSourceUrl: 'https://appclose.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families already on AppClose, or parents who qualify for its hardship / survivor program.',
    whenToPickThem:
      'Stay on AppClose if you already use it and both parents will pay, or if you qualify for a waiver. Choose Harbor if you want $49.99/year per person, Temperature Check, GPS check-ins, and web plus mobile. AppClose is no longer the free default.',
    verdict:
      'AppClose was the usual “free” recommendation through 2025. In 2026 it charges each parent. Harbor is now the clearer low-price all-in-one for families who do not have a waiver.',
    vsHarbor:
      'Both cover messaging, a calendar, and expenses. AppClose now bills about $8–$9 per parent per month. Harbor is $9.99/month or $49.99/year per person, with Temperature Check, receipt OCR, GPS check-ins, and court-ready exports.',
    pros: [
      'Familiar budget brand with a long free-app history',
      'Hardship and survivor fee waivers',
      'Core messaging, calendar, and expense requests',
    ],
    cons: [
      'No longer free for most families as of 2026',
      'Per-parent pricing; two parents pay twice',
      'No Temperature Check; thinner court-ready export workflow',
    ],
    featureMatrix: {
      householdPrice: { text: '~$7.99–$8.99/parent/month after the 2026 paywall' },
      billingUnit: { text: 'Per parent', rating: 'good' },
      messaging: { text: 'Documented messaging', rating: 'good' },
      messageSearchBookmarks: {
        text: 'Search conversations and bookmark messages',
        rating: 'good',
      },
      toneCheck: { text: 'No tone checker', rating: 'bad' },
      expenseTracking: { text: 'Expense and reimbursement requests', rating: 'good' },
      custodyCalendar: { text: 'Shared calendar', rating: 'good' },
      soloUse: { text: 'Works with one parent', rating: 'good' },
      gpsCheckIns: { text: 'Not Harbor-style GPS exchange tracking', rating: 'bad' },
      professionalAccess: {
        text: 'Limited professional portal compared with Harbor',
        rating: 'mixed',
      },
      courtReadyRecords: {
        text: 'Certified records available; less of a one-tap export workflow',
        rating: 'mixed',
      },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is AppClose still free in 2026?',
        answer:
          'Not for most families. AppClose moved core features behind a per-parent subscription in 2026 (about $7.99/month on web or $8.99 in-app). It still offers hardship and survivor waivers. Confirm current terms on appclose.com.',
      },
      {
        question: 'Is Harbor cheaper than AppClose for two parents?',
        answer:
          'Usually yes. Harbor is $49.99/year per person. AppClose is about $8–$9 per parent per month, so two paid AppClose seats are about $192–$216/year. Confirm current AppClose pricing on their site.',
      },
      {
        question: 'Does AppClose have a tone checker like Harbor?',
        answer:
          'No. Harbor Temperature Check scores draft tone and offers a rewrite before send.',
      },
      {
        question: 'Can I search and bookmark messages in Harbor and AppClose?',
        answer:
          'Yes on both. Harbor and AppClose both let you search conversations and bookmark important messages. Harbor organizes saved messages into private bookmark folders.',
      },
      {
        question: 'Can I switch from AppClose to Harbor?',
        answer:
          'Yes. Start Harbor solo, invite the other parent, and keep messaging, expenses, and the calendar in the same household.',
      },
      {
        question: 'Which app is better if I only need a free tool?',
        answer:
          'Neither is a full free all-in-one anymore. Harbor has a 7-day trial then $49.99/year. AppClose waives fees in some hardship cases. Cozi remains a free-leaning family calendar, not a records app.',
      },
    ],
  },
  {
    slug: 'coparently',
    name: 'Coparently',
    tagline: 'Long-running schedule-and-plan app with a quieter product footprint in 2026.',
    seoTitle: 'Harbor vs Coparently: Co-Parenting App Comparison (2026)',
    seoDescription:
      'Compare Harbor and Coparently on pricing, messaging, expenses, Temperature Check, and daily co-parent workflows.',
    pricingMonthly: 'Historically listed around $9.99/month',
    pricingAnnual: 'Annual pricing and current availability vary; verify on their site',
    pricingSourceUrl: 'https://www.coparently.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families already using Coparently for a simple schedule.',
    whenToPickThem:
      'Stay on Coparently only if it already does the job and both parents like it. Choose Harbor for a current iOS/Android/web app with Temperature Check, receipt scanning, GPS check-ins, and $49.99/year per-person pricing.',
    verdict:
      'Coparently covers fundamentals. Harbor is the more current daily operations app: expenses, tone check, exports, and published $49.99/year per-person pricing.',
    vsHarbor:
      'Both target separated parents coordinating a schedule. Harbor is the active product: documented chat, expense OCR, Temperature Check, GPS check-ins, and $49.99/year per person. Coparently is a legacy option; confirm it is still sold and supported before you depend on it.',
    pros: [
      'Longstanding name in co-parenting software',
      'Simple schedule-and-plan model',
      'Familiar to some families who adopted it years ago',
    ],
    cons: [
      'Product activity and public pricing are less clear in 2026',
      'No Temperature Check or GPS exchange check-ins',
      'Weaker expense-to-export workflow than Harbor',
    ],
    featureMatrix: {
      householdPrice: { text: 'Historically ~$9.99/month; confirm current offers' },
      billingUnit: { text: 'Typically one family subscription', rating: 'good' },
      messaging: { text: 'Basic co-parent messaging', rating: 'mixed' },
      messageSearchBookmarks: { text: 'No published search or bookmarks', rating: 'bad' },
      toneCheck: { text: 'No tone checker', rating: 'bad' },
      expenseTracking: { text: 'Basic expense tracking', rating: 'mixed' },
      custodyCalendar: { text: 'Schedule and parenting-plan tools', rating: 'good' },
      soloUse: { text: 'Usable by one parent', rating: 'good' },
      gpsCheckIns: { text: 'No', rating: 'bad' },
      professionalAccess: { text: 'Limited', rating: 'mixed' },
      courtReadyRecords: {
        text: 'Some reports; not Harbor’s one-tap export set',
        rating: 'mixed',
      },
      platforms: { text: 'Mobile and web; verify current store listings', rating: 'mixed' },
    },
    faqs: [
      {
        question: 'Is Coparently still an option in 2026?',
        answer:
          'Some families still use it. Check the current site and store listings before you switch or stay. Availability and pricing have been less transparent than Harbor’s.',
      },
      {
        question: 'Is Harbor cheaper than Coparently?',
        answer:
          'Harbor is $49.99/year per person. Coparently has historically listed about $9.99/month. Compare live prices before you decide.',
      },
      {
        question: 'Does Coparently have a tone checker?',
        answer: 'No. Harbor Temperature Check is the tone-before-send feature.',
      },
      {
        question: 'Does Coparently have message search and bookmarks?',
        answer:
          'Not as a published feature. Harbor lets you search past messages and save them to bookmark folders.',
      },
      {
        question: 'Which app is better for records?',
        answer:
          'Harbor is built for one-tap message, expense, and calendar exports. Coparently is more of a schedule-and-plan tool.',
      },
    ],
  },
  {
    slug: 'custody-x-change',
    name: 'Custody X Change',
    tagline:
      'Custody-schedule and parenting-plan software with co-parent messaging and expenses, but no Temperature Check, bookmarks, or receipt OCR.',
    seoTitle: 'Custody X Change Alternative: Harbor vs Custody X Change (2026)',
    seoDescription:
      'Custody X Change is for parenting-plan calendars. Harbor is the daily co-parenting app: messaging, expenses, Temperature Check, and $49.99/year per-person pricing.',
    pricingMonthly: 'Sold mainly as annual planning software, not a monthly messenger',
    pricingAnnual: 'Commonly about $147–$247/year depending on package',
    pricingSourceUrl: 'https://www.custodyxchange.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Parents who need detailed custody calendars and parenting-plan printouts.',
    whenToPickThem:
      'Use Custody X Change when you are drafting a formal parenting-plan calendar and want its planning outputs. Use Harbor for the years after: messaging, expenses, Temperature Check, and a live custody calendar. Many families use a planner once, then live in Harbor. Harbor also has a free parenting-plan builder.',
    verdict:
      'Custody X Change is a planning-first tool whose messaging and expenses are more basic. Harbor is the daily operations app with Temperature Check, receipt OCR, bookmarks, and $49.99/year per-person pricing — it replaces the “second app.”',
    vsHarbor:
      'Custody X Change produces detailed custody calendars and plan documents, and includes co-parent messaging and expense tracking — but without Temperature Check, receipt OCR, or message bookmarks. Harbor is $49.99/year for messaging, expenses, Temperature Check, GPS check-ins, and a live calendar you actually use each week. Harbor’s free parenting-plan builder covers many planning needs without a second subscription.',
    pros: [
      'Detailed custody calendar and parenting-plan outputs',
      'Known in custody-planning workflows',
      'Useful when you need formal schedule artifacts',
    ],
    cons: [
      'Messaging and expenses are more basic than Harbor’s (no Temperature Check, OCR, or bookmarks)',
      'You still need another tool for the daily workflow Harbor covers',
      'Per-parent pricing: each parent pays unless one buys the Gold add-on',
    ],
    featureMatrix: {
      householdPrice: { text: 'About $147–$247/year for planning packages' },
      billingUnit: { text: 'Per-parent subscription; one can cover both via a Gold add-on', rating: 'mixed' },
      messaging: {
        text: 'Co-parent messaging with read receipts and a hostility monitor; no Temperature Check or bookmarks',
        rating: 'mixed',
      },
      messageSearchBookmarks: { text: 'Keyword search and labels, but no bookmarking', rating: 'mixed' },
      toneCheck: { text: 'Flags hostile words only; no tone rewrite', rating: 'bad' },
      expenseTracking: { text: 'Expense ledger with splits, payments, and exports; no receipt OCR', rating: 'mixed' },
      custodyCalendar: { text: 'Detailed custody and holiday planning calendars', rating: 'good' },
      soloUse: {
        text: 'Yes: planning is often done by one parent or an attorney',
        rating: 'good',
      },
      gpsCheckIns: { text: 'No', rating: 'bad' },
      professionalAccess: {
        text: 'Dedicated professional accounts with read-only message access; client accounts are free',
        rating: 'good',
      },
      courtReadyRecords: {
        text: 'Printable schedule, plan, message, and expense reports',
        rating: 'good',
      },
      platforms: { text: 'Browser-installed PWA on all devices; no native App Store app', rating: 'mixed' },
    },
    faqs: [
      {
        question: 'Is Custody X Change a messaging app like Harbor?',
        answer:
          'It has co-parent messaging, but more basic than Harbor’s — no Temperature Check, bookmarks, or tone rewrite.',
      },
      {
        question: 'Should I use both Harbor and Custody X Change?',
        answer:
          'Some families use a planner once, then run daily life in Harbor. Harbor also has a free parenting-plan builder at harborcoparent.com/parenting-plan.',
      },
      {
        question: 'Which is cheaper for ongoing co-parenting?',
        answer:
          'Harbor is $49.99/year per person and covers messaging, expenses, and the calendar. Custody X Change is a separate planning purchase.',
      },
      {
        question: 'Does Custody X Change have a tone checker?',
        answer:
          'Not like Harbor’s. It flags hostile words before you send, but doesn’t rewrite tone the way Temperature Check does.',
      },
      {
        question: 'Does Custody X Change have message search and bookmarks?',
        answer:
          'It has keyword search and labels, but no bookmarking. Harbor’s messages are searchable and bookmarkable.',
      },
    ],
  },
  {
    slug: 'fayr',
    name: 'Fayr',
    tagline: 'Co-parenting app with communication and scheduling; public pricing is less clear.',
    seoTitle: 'Harbor vs Fayr: Co-Parenting App Comparison (2026)',
    seoDescription:
      'Harbor vs Fayr on pricing transparency, Temperature Check, expenses, GPS check-ins, and court-ready exports. Harbor is $49.99/year per person.',
    pricingMonthly: 'Public list pricing is limited; check Fayr’s current offers',
    pricingAnnual: 'Annual options may exist depending on the current plan page',
    pricingSourceUrl: 'https://www.fayr.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families already evaluating Fayr as a newer brand.',
    whenToPickThem:
      'Consider Fayr if you already prefer its interface and can confirm live pricing. Choose Harbor when you want published pricing ($49.99/year per person), Temperature Check, GPS check-ins, and court-ready exports.',
    verdict:
      'Fayr can cover core coordination. Harbor is easier to recommend because the price, platforms, and records workflow are public and specific.',
    vsHarbor:
      'Both target co-parent messaging and scheduling. Harbor publishes $9.99/month or $49.99/year per person and includes Temperature Check, receipt OCR, GPS check-ins, and one-tap exports. Fayr’s public pricing is harder to pin down, which makes side-by-side cost comparisons unreliable.',
    pros: [
      'Modern co-parent app positioning',
      'Communication and scheduling workflows',
      'Alternative brand for comparison shoppers',
    ],
    cons: [
      'Public pricing is less straightforward than Harbor’s',
      'No published Temperature Check equivalent',
      'Legal-workflow familiarity varies by market',
    ],
    featureMatrix: {
      householdPrice: { text: 'Not clearly listed; verify on fayr.com' },
      billingUnit: { text: 'Confirm on current Fayr plans', rating: 'mixed' },
      messaging: { text: 'In-app co-parent messaging', rating: 'mixed' },
      messageSearchBookmarks: { text: 'No published search or bookmark tools', rating: 'bad' },
      toneCheck: { text: 'No published tone checker', rating: 'bad' },
      expenseTracking: { text: 'Expense tools available', rating: 'good' },
      custodyCalendar: { text: 'Shared scheduling', rating: 'good' },
      soloUse: { text: 'Typically usable by one parent', rating: 'good' },
      gpsCheckIns: { text: 'Not Harbor-style GPS exchange tracking', rating: 'bad' },
      professionalAccess: { text: 'Limited / varies', rating: 'mixed' },
      courtReadyRecords: { text: 'Weaker public export story than Harbor', rating: 'mixed' },
      platforms: { text: 'Mobile-first; confirm web access', rating: 'mixed' },
    },
    faqs: [
      {
        question: 'Is Fayr cheaper than Harbor?',
        answer:
          'Harbor is $49.99/year per person. Fayr does not publish a stable public price that we can quote. Compare their current checkout with Harbor’s listed plans.',
      },
      {
        question: 'Does Fayr have a tone checker like Harbor Temperature Check?',
        answer:
          'Not as a published equivalent. Harbor Temperature Check scores draft tone and offers a rewrite before send.',
      },
      {
        question: 'Does Fayr have message search and bookmarks?',
        answer:
          'Not as a published feature. Harbor lets you search past messages and save them to bookmark folders.',
      },
      {
        question: 'Which app is clearer for records and exports?',
        answer:
          'Harbor is built around one-tap message, expense, and calendar exports. That workflow is a first-class Harbor feature.',
      },
      {
        question: 'Can I start Harbor with one parent?',
        answer: 'Yes. Harbor works solo until the other parent joins.',
      },
    ],
  },
  {
    slug: 'weparent',
    name: 'WeParent',
    tagline: 'Planner-style co-parenting app with calendars, to-dos, and family-level billing.',
    seoTitle: 'Harbor vs WeParent: Co-Parenting App Comparison (2026)',
    seoDescription:
      'Compare Harbor and WeParent on pricing, messaging, expenses, Temperature Check, and court-ready exports.',
    pricingMonthly: 'Often listed near $9.99–$19.99/month depending on plan and billing page',
    pricingAnnual: 'Discounted annual billing is sometimes offered; confirm live pricing',
    pricingSourceUrl: 'https://weparent.app',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families who want planner-style lists and organization more than records exports.',
    whenToPickThem:
      'Pick WeParent if you already like its planner UI and both parents will use the lists. Pick Harbor for Temperature Check, GPS check-ins, receipt OCR, and $49.99/year per-person pricing with court-ready exports.',
    verdict:
      'WeParent is a solid organizer. Harbor usually wins when expenses, tone checking, and exportable records matter as much as the calendar.',
    vsHarbor:
      'Both include a calendar and communication. Harbor is $49.99/year per person with Temperature Check, GPS check-ins, and one-tap exports. WeParent leans planner-and-to-do; family pricing is often higher than Harbor’s annual plan.',
    pros: [
      'Planner-oriented calendars and to-dos',
      'Family-level billing on many plans',
      'Cross-platform use',
    ],
    cons: [
      'Usually more expensive than Harbor’s annual per-person plan',
      'No Temperature Check',
      'Weaker court-ready export workflow',
    ],
    featureMatrix: {
      householdPrice: { text: 'Often ~$10–$20/month per family; confirm live plans' },
      billingUnit: { text: 'Typically household / family', rating: 'good' },
      messaging: { text: 'In-app messaging', rating: 'mixed' },
      messageSearchBookmarks: { text: 'No published search or bookmark tools', rating: 'bad' },
      toneCheck: { text: 'No tone checker', rating: 'bad' },
      expenseTracking: { text: 'Expense tracking available', rating: 'good' },
      custodyCalendar: { text: 'Planner-style custody calendar', rating: 'good' },
      soloUse: { text: 'Works with one parent', rating: 'good' },
      gpsCheckIns: { text: 'No Harbor-style GPS exchange tracking', rating: 'bad' },
      professionalAccess: { text: 'Limited', rating: 'mixed' },
      courtReadyRecords: { text: 'Organization first; exports are secondary', rating: 'mixed' },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is Harbor cheaper than WeParent?',
        answer:
          'Harbor is $49.99/year per person. WeParent is often listed near $10–$20/month. Harbor is usually lower for a full year. Confirm WeParent’s current page.',
      },
      {
        question: 'Does WeParent have a tone checker?',
        answer: 'No. Harbor Temperature Check scores tone and offers a rewrite before send.',
      },
      {
        question: 'Does WeParent have message search and bookmarks?',
        answer:
          'Not as a published feature. Harbor lets you search past messages and save them to bookmark folders.',
      },
      {
        question: 'Which app is better for expenses and records?',
        answer:
          'Harbor keeps splits, receipt scans, payments, and exports in one ledger next to messaging and the calendar.',
      },
      {
        question: 'Can both apps handle a custody calendar?',
        answer: 'Yes. Harbor also syncs to Google, Apple, and Outlook.',
      },
    ],
  },
  {
    slug: 'cozi',
    name: 'Cozi',
    tagline: 'Shared family calendar and lists, not a co-parenting records app.',
    seoTitle: 'Harbor vs Cozi: Co-Parenting App vs Family Organizer (2026)',
    seoDescription:
      'Cozi is a family calendar. Harbor is the co-parenting app for documented messaging, expenses, Temperature Check, and court-ready exports at $49.99/year.',
    pricingMonthly: 'Free ad-supported tier; Gold and Max are paid upgrades',
    pricingAnnual: 'Paid plans commonly about $39–$80/year for the household',
    pricingSourceUrl: 'https://www.cozi.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Low-conflict households that only need a shared calendar and grocery lists.',
    whenToPickThem:
      'Keep Cozi if you and the other parent get along and only need a family calendar. Switch to Harbor when you need documented messages, expense splits, Temperature Check, or exportable records. Cozi is not a substitute for a co-parenting records app.',
    verdict:
      'Cozi is a good family organizer. It is not built for separated-parent documentation. Harbor is the purpose-built co-parenting app.',
    vsHarbor:
      'Cozi shares calendars and lists across a household. Harbor is for separated parents: timestamped messaging, expense splits, Temperature Check, GPS check-ins, and court-ready exports for $49.99/year. If the problem is conflict, money, or a record (not groceries), use Harbor.',
    pros: [
      'Easy shared family calendar',
      'Free tier lowers adoption friction',
      'Strong for lists and household logistics',
    ],
    cons: [
      'Not a documented co-parent messaging system',
      'No expense ledger or court-ready exports',
      'No Temperature Check or GPS exchange check-ins',
    ],
    featureMatrix: {
      householdPrice: { text: 'Free tier; paid Gold/Max often ~$39–$80/year' },
      billingUnit: { text: 'Household', rating: 'good' },
      messaging: { text: 'Not documented co-parent messaging', rating: 'bad' },
      messageSearchBookmarks: { text: 'No', rating: 'bad' },
      toneCheck: { text: 'No', rating: 'bad' },
      expenseTracking: { text: 'No co-parent expense ledger', rating: 'bad' },
      custodyCalendar: {
        text: 'Shared family calendar, not a custody-records calendar',
        rating: 'mixed',
      },
      soloUse: { text: 'Yes, as a personal/family calendar', rating: 'good' },
      gpsCheckIns: { text: 'No', rating: 'bad' },
      professionalAccess: { text: 'No', rating: 'bad' },
      courtReadyRecords: { text: 'No', rating: 'bad' },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is Cozi a co-parenting app?',
        answer:
          'No. Cozi is a general family organizer. Harbor is built for separated parents who need documented messaging, expenses, and exports.',
      },
      {
        question: 'Can I use Cozi after a separation?',
        answer:
          'You can share a calendar if conflict is low. When you need a record of messages or money, use a dedicated app like Harbor.',
      },
      {
        question: 'Is Harbor more expensive than Cozi?',
        answer:
          'Harbor is $49.99/year. Cozi has a free tier and paid plans around $39–$80/year. You are paying Harbor for records, expenses, and tone check, not just a calendar.',
      },
      {
        question: 'Does Cozi have a tone checker?',
        answer: 'No. Temperature Check is a Harbor messaging feature.',
      },
      {
        question: 'Does Cozi have message search and bookmarks?',
        answer:
          'No. Cozi is a family calendar, not a documented messenger. Harbor lets you search and bookmark co-parent messages.',
      },
    ],
  },
  {
    slug: 'supportpay',
    name: 'SupportPay',
    tagline: 'Child-support and reimbursement tracker, not a full co-parenting app.',
    seoTitle: 'SupportPay vs Harbor: expense app vs full co-parenting app',
    seoDescription:
      'SupportPay tracks reimbursements. Harbor is the all-in-one co-parenting app: messaging, calendar, expenses, Temperature Check, and $49.99/year per-person pricing.',
    pricingMonthly: 'Varies by plan and payer setup; confirm on supportpay.com',
    pricingAnnual: 'Contract-style annual options may apply; verify live pricing',
    pricingSourceUrl: 'https://www.supportpay.com',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Families who only want a reimbursement and support-payment ledger.',
    whenToPickThem:
      'Use SupportPay if shared payments are your only problem and you already have messaging and a calendar elsewhere. Use Harbor if you want expenses plus documented chat, a custody calendar, Temperature Check, and exports in one $49.99/year per-person app.',
    verdict:
      'SupportPay is expense-centric. Harbor covers expenses and the rest of daily co-parenting, so most families do not need a second app.',
    vsHarbor:
      'SupportPay specializes in support and reimbursement tracking. Harbor includes expense splits and receipt OCR next to messaging, a custody calendar, Temperature Check, and court-ready exports for $49.99/year per person. Choose SupportPay only if money is the sole job.',
    pros: [
      'Focused reimbursement and support-payment workflows',
      'Useful when child support tracking is the only need',
      'Recognized in some family-finance conversations',
    ],
    cons: [
      'Not a messaging or custody-calendar replacement',
      'Families often still need a second app',
      'Pricing is less straightforward than Harbor’s published plans',
    ],
    featureMatrix: {
      householdPrice: { text: 'Varies by plan; confirm on supportpay.com' },
      billingUnit: { text: 'Plan- and payer-dependent', rating: 'mixed' },
      messaging: { text: 'Not a co-parent messenger', rating: 'bad' },
      messageSearchBookmarks: { text: 'No', rating: 'bad' },
      toneCheck: { text: 'No', rating: 'bad' },
      expenseTracking: {
        text: 'Support and reimbursement tracking is the product',
        rating: 'good',
      },
      custodyCalendar: { text: 'No full custody calendar', rating: 'bad' },
      soloUse: { text: 'Yes for the paying / requesting parent', rating: 'good' },
      gpsCheckIns: { text: 'No', rating: 'bad' },
      professionalAccess: {
        text: 'Finance-oriented sharing; not Harbor’s read-only portal',
        rating: 'mixed',
      },
      courtReadyRecords: {
        text: 'Payment history exports; not a full message/calendar record',
        rating: 'mixed',
      },
      platforms: { text: 'iOS, Android, and web', rating: 'good' },
    },
    faqs: [
      {
        question: 'Is SupportPay a full co-parenting app?',
        answer:
          'No. It is an expense and reimbursement platform. Harbor is messaging, calendar, expenses, and exports together.',
      },
      {
        question: 'Can Harbor replace SupportPay?',
        answer:
          'For many families, yes: Harbor tracks splits, payments, and receipts, and also keeps chat and the calendar in the same app.',
      },
      {
        question: 'Is Harbor cheaper than SupportPay?',
        answer:
          'Harbor is $49.99/year per person. SupportPay pricing varies by plan; compare their checkout with Harbor’s published price.',
      },
      {
        question: 'Does SupportPay have a tone checker?',
        answer: 'No. Temperature Check lives in Harbor messaging.',
      },
      {
        question: 'Does SupportPay have message search and bookmarks?',
        answer:
          'No. SupportPay is an expense platform. Harbor includes searchable, bookmarkable messaging next to expenses and the calendar.',
      },
    ],
  },
  {
    slug: 'hellodivorce',
    name: 'Hello Divorce',
    tagline: 'Paid online divorce filing, not a co-parenting app for the years after.',
    seoTitle: 'Harbor vs Hello Divorce: Costs, DIY Divorce & What Comes After (2026)',
    seoDescription:
      'Hello Divorce sells divorce-completion plans. Harbor is the $49.99/year co-parenting app you use after, plus free parenting-plan and state checklist tools.',
    pricingMonthly: 'One-time divorce plans, commonly about $99 to $2,500+ depending on plan',
    pricingAnnual: 'One-time plan plus optional hourly legal and expert services',
    pricingSourceUrl: 'https://hellodivorce.com/pricing',
    pricingVerifiedDate: '2026-08-17',
    bestFor: 'Couples who want a paid, guided online divorce filing service in the states Hello Divorce serves.',
    whenToPickThem:
      'Use Hello Divorce when you want paid hand-holding to complete a divorce filing. Use Harbor for the years after the decree (messaging, expenses, calendar, Temperature Check) at $49.99/year. Harbor also links free official state forms and a free parenting-plan builder; it is not a paid filing service.',
    verdict:
      'Hello Divorce finishes a divorce. Harbor runs co-parenting afterward. They are not substitutes; most families who file still need an app like Harbor.',
    vsHarbor:
      'Hello Divorce charges for divorce paperwork help and legal add-ons. Harbor is $49.99/year for documented messaging, expenses, a custody calendar, and Temperature Check. Harbor also points to free official forms and a free parenting-plan builder. If you need a filing concierge, that is Hello Divorce. If you need the daily app after, that is Harbor.',
    pros: [
      'Structured, supported divorce filing workflow',
      'Optional attorneys, mediators, and financial experts',
      'Flat-fee plans can cost less than full representation',
    ],
    cons: [
      'Divorce plans cost hundreds to thousands of dollars',
      'Full service is limited to certain states',
      'Ends at the decree: no daily co-parenting app',
    ],
    featureMatrix: {
      householdPrice: { text: 'One-time $99–$2,500+ filing plans, not a monthly co-parent app' },
      billingUnit: { text: 'Per divorce plan / optional hourly experts', rating: 'mixed' },
      messaging: { text: 'Not a co-parent messenger', rating: 'bad' },
      messageSearchBookmarks: { text: 'No', rating: 'bad' },
      toneCheck: { text: 'No', rating: 'bad' },
      expenseTracking: { text: 'Not a shared expense ledger', rating: 'bad' },
      custodyCalendar: { text: 'Not a live custody calendar', rating: 'bad' },
      soloUse: { text: 'Filing can be started by one spouse', rating: 'good' },
      gpsCheckIns: { text: 'No', rating: 'bad' },
      professionalAccess: {
        text: 'Paid attorneys and experts inside the divorce product',
        rating: 'mixed',
      },
      courtReadyRecords: {
        text: 'Divorce paperwork, not an ongoing message/expense record',
        rating: 'mixed',
      },
      platforms: { text: 'Web', rating: 'mixed' },
    },
    faqs: [
      {
        question: 'Is Harbor a replacement for Hello Divorce?',
        answer:
          'Not as a paid filing concierge. Harbor’s free state checklists link official court forms. Hello Divorce sells guided filing and legal add-ons. After the decree, Harbor is the co-parenting app; Hello Divorce is not.',
      },
      {
        question: 'Can I file for divorce without paying for a service?',
        answer:
          'Often yes if the case is uncontested. States publish free official forms and fee waivers. Harbor’s divorce checklists link those sources. This is not legal advice.',
      },
      {
        question: 'What does Harbor offer after the divorce that Hello Divorce does not?',
        answer:
          'Harbor is $49.99/year for a custody calendar, expense splitting, documented messaging, Temperature Check, and court-ready exports. Divorce services stop at the decree.',
      },
      {
        question: 'Does Hello Divorce have message search and bookmarks?',
        answer:
          'No. Hello Divorce is a filing service. Harbor is the daily co-parenting app with searchable, bookmarkable messages.',
      },
      {
        question: 'Should I use Harbor if a court order names another app?',
        answer:
          'Use the named app. Harbor is for families who can choose their co-parenting platform.',
      },
    ],
  },
];

export function isCompetitorSlug(value: string): value is CompetitorSlug {
  return (COMPETITOR_SLUGS as readonly string[]).includes(value);
}

export function getCompetitorBySlug(slug: CompetitorSlug): CompetitorProfile {
  const competitor = COMPETITORS.find((item) => item.slug === slug);
  if (!competitor) {
    throw new Error(`Unknown competitor slug: ${slug}`);
  }
  return competitor;
}

export function formatPricingVerifiedDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
